import React, { useEffect, useRef, useState } from 'react';
import ReactPaginate from 'react-paginate';
import Modal from 'react-bootstrap/Modal';
import PromoCodeFilters from './PromoCodeFilters';
import usePromoCodeList from './usePromoCodeList';
import AdminPromoDetailsModal from './AdminPromoDetailsModal';
import { getPromoStatus, promoStatuses } from './promoListQuery';
import '../../styles/common.css';
import axios from 'axios';
import Header from '../Header';
import Footer from '../Footer';
import AdminPromoWheelSettings from './AdminPromoWheelSettings';
import { checkIsAdminLoggined, formatPrice } from '../../utils/utils';
import { getApiErrorText } from '../../utils/apiResponse';
import { savePromoCode } from '../../api/promoCodesApi';
import { promoDateInput, promoDateLabel, promoDateToUtc } from '../../utils/promoDates';
import '../../styles/promo-codes.css';

const emptyPromo = () => ({
  code: '', description: '', isActive: true, discountType: 'PERCENT', discountValue: '10',
  minOrderAmount: '', maxDiscountAmount: '', startsAt: '', expiresAt: '',
  usageLimitTotal: '', usageLimitPerUser: '', appliesToSaleItems: false,
  combinesWithPersonalDiscount: false, categoryScope: 'ALL', includeSubcategories: true, categoryIds: [],
});
const numericFields = ['discountValue', 'minOrderAmount', 'maxDiscountAmount', 'usageLimitTotal', 'usageLimitPerUser'];
export default function AdminPromoCodesPage(props) {
  const { items, total, loading, error: listError, query, updateQuery, resetQuery, reload } = usePromoCodeList();
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(null);
  const [error, setError] = useState('');
  const [formError, setFormError] = useState('');
  const [busy, setBusy] = useState(false);
  const [details, setDetails] = useState(null);
  const saving = useRef(false);

  useEffect(() => {
    checkIsAdminLoggined(props);
    axios.get('/api/v1/category').then(response => setCategories(response.data?.data || []))
      .catch(reason => setError(getApiErrorText(reason, 'Не вдалося завантажити категорії')));
    // Authentication is verified again by every protected API endpoint.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const edit = item => {
    setFormError('');
    setForm(item ? { ...emptyPromo(), ...item, startsAt: promoDateInput(item.startsAt), expiresAt: promoDateInput(item.expiresAt),
      ...Object.fromEntries(numericFields.map(field => [field, item[field] ?? ''])) } : emptyPromo());
  };
  const update = (field, value) => setForm(previous => ({ ...previous, [field]: value }));
  const save = async event => {
    event.preventDefault();
    if (saving.current) return;
    setFormError('');
    let body;
    try {
      body = { ...form, code: form.code.trim().toUpperCase(), startsAt: promoDateToUtc(form.startsAt), expiresAt: promoDateToUtc(form.expiresAt) };
      numericFields.forEach(field => {
        const value = String(form[field] ?? '').trim();
        if (value && (!/^\d+$/.test(value) || Number(value) > 2147483647)) throw new Error('Суми, відсотки та ліміти мають бути цілими числами в допустимих межах');
        body[field] = value === '' ? null : Number(value);
      });
      if (body.discountType === 'FIXED_UAH') body.maxDiscountAmount = null;
    } catch (reason) { setFormError(reason.message); return; }
    saving.current = true; setBusy(true);
    try { await savePromoCode(body); setForm(null); reload(); }
    catch (reason) { setFormError(getApiErrorText(reason)); }
    finally { saving.current = false; setBusy(false); }
  };
  const toggle = async item => {
    if (saving.current) return;
    saving.current = true; setBusy(true);
    try {
      const saved = await savePromoCode({ ...item, isActive: !item.isActive });
      const updated = { ...item, isActive: !item.isActive, ...saved, status: null };
      updated.status = getPromoStatus(updated);
      setDetails(current => current?.id === item.id ? updated : current);
      reload();
    } finally { saving.current = false; setBusy(false); }
  };
  const numberInput = (field, label, options = {}) => <div className='form-group col-md-6'>
    <label htmlFor={`promo-${field}`}>{label}</label>
    <input id={`promo-${field}`} type='number' min={field === 'minOrderAmount' ? 0 : 1} step='1'
      max={field === 'discountValue' && form.discountType === 'PERCENT' ? 100 : 2147483647}
      className='form-control' value={form[field]} disabled={busy || options.disabled}
      placeholder={options.placeholder || 'Без обмеження'} required={options.required}
      onChange={event => update(field, event.target.value)} />
    {options.hint && <small className='text-muted'>{options.hint}</small>}
  </div>;
  const check = (field, label) => <div className='custom-control custom-checkbox mb-2'>
    <input id={`promo-${field}`} type='checkbox' className='custom-control-input' checked={Boolean(form[field])}
      disabled={busy} onChange={event => update(field, event.target.checked)} />
    <label className='custom-control-label' htmlFor={`promo-${field}`}>{label}</label>
  </div>;

  const sort = field => updateQuery({ sortBy: field, sortOrder: query.sortBy === field && query.sortOrder === 'asc' ? 'desc' : 'asc' });
  const sortHeader = (field, label) => <th scope='col' aria-sort={query.sortBy === field ? (query.sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}>
    <button type='button' className='promo-sort-button' onClick={() => sort(field)}>{label}
      {query.sortBy === field && <i className={`bi bi-arrow-${query.sortOrder === 'asc' ? 'up' : 'down'} ml-1`} aria-hidden='true' />}
    </button>
  </th>;
  const pageCount = Math.ceil(total / query.limit);

  return <div className='container admin-promo-page'>
    <div className='row'><div className='col p-0'><Header /></div></div>
    <PromoCodeFilters query={query} onChange={updateQuery} onReset={resetQuery} />
    <div className='d-flex flex-wrap justify-content-between align-items-center mb-3'>
      <h5 className='font-weight-bold mb-0'>Усього промокодів: {total}</h5>
      <div className='d-flex flex-wrap align-items-center' style={{ gap: '0.5rem' }}>
        <AdminPromoWheelSettings />
        <button type='button' className='btn btn-dark' disabled={busy} onClick={() => edit(null)}>Створити промокод</button>
      </div>
    </div>
    {(error || listError) && <div className='alert alert-danger' role='alert'>{error || listError}</div>}
    <Modal show={Boolean(form)} onHide={() => { if (!saving.current) setForm(null); }} size='lg' centered animation={false} className='promo-admin-modal' dialogClassName='promo-editor-dialog'
      backdrop={busy ? 'static' : true} keyboard={!busy} aria-labelledby='promo-editor-title'>
      <Modal.Header>
        <Modal.Title as='h5' id='promo-editor-title'>{form?.id ? `Промокод ${form.code}` : 'Новий промокод'}</Modal.Title>
        <button type='button' className='close' aria-label='Закрити' disabled={busy} onClick={() => setForm(null)}><span aria-hidden='true'>×</span></button>
      </Modal.Header>
      {form && <form className='admin-promo-editor' onSubmit={save}>
      <Modal.Body>
      <section className='promo-editor-section'>
      <h6 className='promo-editor-section-title'>Основне</h6>
      <div className='form-row'>
        <div className='form-group col-md-6'><label htmlFor='promo-code'>Код</label>
          <input id='promo-code' className='form-control' required maxLength={64} pattern={'[A-Za-z0-9_\\-]+'}
            value={form.code} readOnly={Boolean(form.id)} disabled={busy} autoComplete='off'
            onChange={event => update('code', event.target.value.toUpperCase())} />
          <small className='text-muted'>Латиниця, цифри, дефіс або підкреслення.</small>
        </div>
        <div className='form-group col-md-6'><label htmlFor='promo-type'>Тип знижки</label>
          <select id='promo-type' className='form-control' value={form.discountType} disabled={busy}
            onChange={event => update('discountType', event.target.value)}>
            <option value='PERCENT'>Відсоткова</option><option value='FIXED_UAH'>Фіксована сума у гривнях</option>
          </select>
        </div>
      </div>
      </section>
      <section className='promo-editor-section'>
      <h6 className='promo-editor-section-title'>Знижка</h6>
      <div className='form-row'>
        {numberInput('discountValue', form.discountType === 'PERCENT' ? 'Знижка, %' : 'Знижка, грн', { required: true })}
        {numberInput('maxDiscountAmount', 'Максимальна знижка, грн', { disabled: form.discountType !== 'PERCENT' })}
        {numberInput('minOrderAmount', 'Мінімальна сума, грн', { hint: 'Сума відповідних товарів після інших знижок, без доставки.' })}
      </div>
      </section>
      <section className='promo-editor-section'>
      <h6 className='promo-editor-section-title'>Строк дії та ліміти</h6>
      <div className='form-row'>
        {numberInput('usageLimitTotal', 'Загальна кількість використань')}
        {numberInput('usageLimitPerUser', 'Використань на покупця')}
        <div className='col-12 small text-muted mb-3'>Для одноразового коду встановіть загальний ліміт 1. Ліміт на покупця потребує авторизації.</div>
        <div className='form-group col-md-6'><label htmlFor='promo-starts'>Початок дії (Київ)</label>
          <input id='promo-starts' type='datetime-local' className='form-control' value={form.startsAt} disabled={busy} onChange={event => update('startsAt', event.target.value)} />
        </div>
        <div className='form-group col-md-6'><label htmlFor='promo-expires'>Завершення дії (Київ)</label>
          <input id='promo-expires' type='datetime-local' className='form-control' value={form.expiresAt} disabled={busy} onChange={event => update('expiresAt', event.target.value)} />
          <small className='text-muted'>Порожні дати — без обмеження строку.</small>
        </div>
      </div>
      </section>
      <section className='promo-editor-section'>
      <h6 className='promo-editor-section-title'>Умови застосування</h6>
      {check('isActive', 'Промокод активний')}
      {check('appliesToSaleItems', 'Діє на товари з акційною знижкою')}
      {check('combinesWithPersonalDiscount', 'Сумується з персональною знижкою')}
      <p className='small text-muted'>Без сумування застосовується вигідніша пропозиція для кожної позиції.</p>
      <div className='form-group'><label htmlFor='promo-scope'>Категорії товарів</label>
        <select id='promo-scope' className='form-control' value={form.categoryScope} disabled={busy} onChange={event => update('categoryScope', event.target.value)}>
          <option value='ALL'>Усі категорії</option><option value='SELECTED'>Лише обрані категорії</option>
        </select>
      </div>
      {form.categoryScope === 'SELECTED' && <>
        {check('includeSubcategories', 'Включати підкатегорії')}
        <div className='admin-promo-categories mb-3'>
          {categories.length === 0 && <div>Немає доступних категорій</div>}
          {categories.map(category => <label key={category.id}>
            <input type='checkbox' disabled={busy} checked={form.categoryIds.includes(category.id)}
              onChange={event => update('categoryIds', event.target.checked ? [...form.categoryIds, category.id] : form.categoryIds.filter(id => id !== category.id))} />
            {category.parentId ? '↳ ' : ''}{category.name.replace(/<[^>]*>/g, '')}
          </label>)}
        </div>
      </>}
      </section>
      <section className='promo-editor-section'>
      <h6 className='promo-editor-section-title'>Для адміністратора</h6>
      <div className='form-group mb-0'><label htmlFor='promo-description'>Внутрішній коментар</label>
        <textarea id='promo-description' rows={2} className='form-control' maxLength={1000} value={form.description || ''} disabled={busy} onChange={event => update('description', event.target.value)} />
      </div>
      </section>
      {formError && <div className='alert alert-danger' role='alert'>{formError}</div>}
      {form.id && <p className='small text-muted mt-3 mb-0'>Зміни застосовуються до наступних оформлень. Створені замовлення зберігають свої умови.</p>}
      </Modal.Body>
      <Modal.Footer>
        <button type='button' className='btn btn-secondary' disabled={busy} onClick={() => setForm(null)}>Скасувати</button>
        <button type='submit' className='btn btn-dark' disabled={busy}>{busy ? 'Зберігаємо…' : 'Зберегти'}</button>
      </Modal.Footer>
    </form>}
    </Modal>
    {details && <AdminPromoDetailsModal key={details.id} promo={details} categories={categories} busy={busy}
      onClose={() => { if (!saving.current) setDetails(null); }} onToggle={toggle}
      onEdit={item => { setDetails(null); edit(item); }} />}
    {loading ? <p role='status'>Завантажуємо промокоди…</p> : <div className='table-responsive'>
      <table className='table ap-products-table admin-promo-table'><thead><tr>
        {sortHeader('id', 'ID')}{sortHeader('code', 'Код')}<th scope='col'>Статус</th>
        {sortHeader('discountValue', 'Знижка')}{sortHeader('createdAt', 'Згенеровано')}{sortHeader('expiresAt', 'Діє до')}<th scope='col'>Дії</th>
      </tr></thead>
        <tbody>{items.map(item => {
          const status = getPromoStatus(item);
          return <tr key={item.id}>
            <td data-label='ID'>{item.id}</td>
            <td data-label='Код' className='promo-code-cell'><button type='button' className='promo-code-link' title={item.code} onClick={() => setDetails(item)}>{item.code}</button></td>
            <td data-label='Статус' className='promo-list-status'><span className={`promo-status promo-status-${status}`}>{promoStatuses[status]}</span></td>
            <td data-label='Знижка' className='promo-list-discount'>{formatPrice(item.discountValue)}{item.discountType === 'PERCENT' ? '%' : ' грн'}</td>
            <td data-label='Згенеровано' className='promo-list-created'>{item.createdAt ? promoDateLabel(item.createdAt) : '—'}</td>
            <td data-label='Діє до' className='promo-list-expiry'>{item.expiresAt ? promoDateLabel(item.expiresAt) : 'Без строку'}</td>
            <td data-label='Дії' className='promo-list-actions'><button type='button' className='btn btn-sm btn-outline-secondary' onClick={() => setDetails(item)} aria-label={`Деталі промокоду ${item.code}`}>Деталі</button></td>
          </tr>;
        })}{items.length === 0 && <tr><td colSpan={7}>Промокодів не знайдено</td></tr>}</tbody>
      </table>
    </div>}
    <div className='promo-list-pagination mb-4'>
      <span className='small text-muted'>{loading ? 'Завантаження…' : `Показано ${items.length ? (query.page - 1) * query.limit + 1 : 0}–${items.length ? (query.page - 1) * query.limit + items.length : 0} з ${total}`}</span>
      {pageCount > 1 && <nav aria-label='Сторінки промокодів'>
        <ReactPaginate previousLabel='‹' nextLabel='›' breakLabel='…' pageCount={pageCount} marginPagesDisplayed={1} pageRangeDisplayed={3}
          forcePage={Math.min(query.page - 1, pageCount - 1)} onPageChange={({ selected }) => { if (!loading && selected + 1 !== query.page) updateQuery({ page: selected + 1 }); }}
          onClick={() => loading ? false : undefined} containerClassName='pagination mb-0' activeClassName='active' />
      </nav>}
      <label className='promo-page-size' htmlFor='promo-page-size'>На сторінці
        <select id='promo-page-size' className='form-control form-control-sm ml-2' value={query.limit} onChange={event => updateQuery({ limit: Number(event.target.value) })}>
          {[25, 50, 100].map(value => <option key={value} value={value}>{value}</option>)}
        </select>
      </label>
    </div>
    <Footer />
  </div>;
}
